import { useEffect } from 'react';
import { Box } from '@chakra-ui/react';
import axios, { AxiosError } from 'axios';
import { startCase, toLower } from 'lodash';
import { Spinner } from 'components/elements/Spinner';
import { StickyItem } from 'components/elements/StickyItem';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import BasePageWithTopNav from 'components/layout/TopNavigationLayout';
import PageMetadata from 'components/PageMetadata';
import MarketNotInSubscription from 'components/widgets/Error/MarketNotInSubscription';
import PageNotFound from 'components/widgets/Error/PageNotFound';
import SomethingWentWrong from 'components/widgets/Error/SomethingWentWrong';
import {
  TITLE_BAR_HEIGHT,
  CONTENT_TOP_PADDING,
} from 'components/widgets/ProjectDetails/const';
import PageHeader from 'components/widgets/ProjectDetails/PageHeader';
import ProjectDetailsBody from 'components/widgets/ProjectDetails/ProjectDetailsBody';
import SuggestCorrectionFooter from 'components/widgets/SuggestCorrection/Footer';
import { useAnalytics } from 'hooks/useAnalytics';
import { useProjectDetails } from 'hooks/useProjectDetails';
import InvolvedCompanies from 'pages/ProjectDetails/InvolvedCompanies';
import Menu from 'pages/ProjectDetails/Menu';
import { useCurrentRegion } from 'state/currentRegion';
import { useMapState } from 'state/map';
import { ProjectDetailsPageViewSections } from 'utils/analytics/constants';
import { Region } from 'utils/constants/location';

const pageContainerStyle = {
  overflowY: 'auto',
  height: '100%',
  position: 'relative',
};

const pageContentWrapperStyle = {
  paddingRight: (theme: any) => theme.space.fortyEight,
  paddingLeft: (theme: any) => theme.space.fortyEight,
  paddingBottom: (theme: any) => theme.space.fortyEight,
  paddingTop: CONTENT_TOP_PADDING,
};

const pageContentGridStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 3fr 1fr',
  gridTemplateRows: '100%',
  gap: '16px 16px',
  gridTemplateAreas: '. . .',
  maxWidth: '1440px',
  margin: 'auto',
};

const stickyMenuStyle = {
  zIndex: 3,
  top: `calc(${TITLE_BAR_HEIGHT} + ${CONTENT_TOP_PADDING})`,
};

const mainProjectInfoStyle = {
  maxWidth: '784px',
  width: '100%',
  margin: 'auto',
};

const additionalInformationColumnStyle = {
  rowGap: (theme: any) => theme.space.eight,
};

const PDP = ({ projectSlug }: { projectSlug: string }) => {
  const {
    project,
    isLoadingProject,
    projectError,
    projectActivitySummary,
    isLoadingProjectActivitySummary,
    isErrorLoadingActivitySummary,
    involvedCompanies,
    projectSizeInfo,
    projectWorkType,
    landTitleHistory,
    isLoadingLandTitleHistory,
    isProjectsByAddressLoading,
    projectsList,
    numberOfProjectsByAddress,
    setPage,
    projectEvents,
    isLoadingProjectEvents,
  } = useProjectDetails(projectSlug || '');

  const { usePageViewTracking } = useAnalytics();
  const { scrollable, updateScrollable } = useMapState();
  const { setCurrentRegion } = useCurrentRegion();

  usePageViewTracking(
    {
      sectionName: ProjectDetailsPageViewSections.PROJECT_DETAILS,
      subSectionName: projectSlug || undefined,
    },
    [window.location.pathname],
  );

  useEffect(() => {
    if (!scrollable) {
      updateScrollable(true);
    }
  }, [scrollable, updateScrollable]);

  useEffect(() => {
    if (project?.mercator_metropolitan_id) {
      setCurrentRegion(project.mercator_metropolitan_id as Region);
    }
  }, [project?.mercator_metropolitan_id, setCurrentRegion]);

  const combinedError =
    projectError || (project && axios.isAxiosError(project) ? project : null);

  if (!isLoadingProject && combinedError) {
    const axiosError = combinedError as AxiosError;
    if (axiosError?.response?.status === 403) {
      return <MarketNotInSubscription />;
    }
    if (axiosError?.response?.status === 404) {
      return <PageNotFound />;
    }

    return <SomethingWentWrong />;
  }

  if (isLoadingProject || !project) {
    return (
      <BasePageWithTopNav>
        <Spinner />
      </BasePageWithTopNav>
    );
  }

  return (
    <BasePageWithTopNav>
      <PageMetadata title={startCase(toLower(project?.address))} />
      <Box sx={pageContainerStyle}>
        <PageHeader
          address={project?.address || ''}
          city={project?.city || ''}
          state={project?.state || ''}
          coordinates={
            project?.latitude && project?.longitude
              ? { lat: project?.latitude, lng: project?.longitude }
              : undefined
          }
          zipcode={project?.zipcode || ''}
        />
        <Box sx={pageContentWrapperStyle}>
          <Box sx={pageContentGridStyle}>
            <Box>
              <StickyItem sx={stickyMenuStyle} position="sticky">
                <Menu
                  projectId={project.id}
                  projectSlug={projectSlug}
                  address={project.address}
                  city={project.city}
                />
              </StickyItem>
            </Box>
            <Box sx={mainProjectInfoStyle}>
              <ProjectDetailsBody
                project={project}
                isLoadingProject={isLoadingProject}
                projectActivitySummary={projectActivitySummary}
                isLoadingProjectActivitySummary={isLoadingProjectActivitySummary}
                isErrorLoadingActivitySummary={isErrorLoadingActivitySummary}
                projectSizeInfo={projectSizeInfo}
                projectWorkType={projectWorkType}
                landTitleHistory={landTitleHistory}
                isLoadingLandTitleHistory={isLoadingLandTitleHistory}
                isProjectsByAddressLoading={isProjectsByAddressLoading}
                projectsList={projectsList}
                numberOfProjectsByAddress={numberOfProjectsByAddress || 0}
                setPage={setPage}
                projectEvents={projectEvents}
                isLoadingProjectEvents={isLoadingProjectEvents}
              />
            </Box>
            <FlexColumn sx={additionalInformationColumnStyle}>
              <InvolvedCompanies involved={involvedCompanies || null} />
            </FlexColumn>
          </Box>
        </Box>
        <SuggestCorrectionFooter projectId={project?.id} />
      </Box>
    </BasePageWithTopNav>
  );
};

export default PDP;
